import {
  SearchProfileOptionDataFragment,
  SearchProfileType,
  SearchSearchProfiles,
  useDeleteSearchProfileMutation,
  useSearchSearchProfilesLazyQuery
} from "@graphql";
import { AlertDialog, SingleSelect, useSnackbarsPipeline } from "@bonliva-crm/ui";
import { useEffect, useMemo, useState } from "react";
import locale from "@bonliva-crm/locale";
import { SingleValueChip } from "../filters/_select/single-value-chip";

type Option = { label: string; value: SearchProfileOptionDataFragment };

type Props = {
  type: SearchProfileType;
  value: SearchProfileOptionDataFragment | null;
  onChange: (value: SearchProfileOptionDataFragment | null) => void;
  selectProps?: React.ComponentProps<typeof SingleSelect<Option>>;
};

export const ConsultantSearchProfile = (props: Props) => {
  const snack = useSnackbarsPipeline();
  const [searchSearchProfiles, { loading, data }] = useSearchSearchProfilesLazyQuery();
  const [searchText, setSearchText] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [onAgreeAction, setOnAgreeAction] = useState<(() => void) | null>(null);

  const [deleteSearchProfileMutation] = useDeleteSearchProfileMutation({
    refetchQueries: [SearchSearchProfiles.OperationName],
    onCompleted: () => {
      snack.showSuccess(locale.SearchProfileDeleted);
      setAlertOpen(false);
    },
    onError: (error) => {
      console.error(error);
      snack.showError(locale.deleteError(locale.SearchProfile.toLowerCase()));
    }
  });

  const deleteSearchProfile = (id: string) => {
    deleteSearchProfileMutation({
      variables: {
        id: id
      }
    });
  };

  useEffect(() => {
    searchSearchProfiles({
      variables: {
        filter: { type: props.type, text: searchText },
        limit: 100,
        skip: 0
      }
    });
  }, [searchText]);

  const searchProfileOptions = useMemo(() => {
    if (!data) return [];
    return data.SearchProfiles.items.map((item) => ({ label: item.name, value: item }));
  }, [data]);

  return (
    <>
      <SingleSelect<Option>
        value={props.value ? { label: props.value.name, value: props.value } : null}
        onChange={(selected) => props.onChange(selected ? selected.value : null)}
        options={searchProfileOptions}
        isSearchable={true}
        onInputChange={(text) => setSearchText(text)}
        isLoading={loading}
        placeholder={locale.SearchProfiles}
        styleProps={{
          control: { showBorder: true },
          menu: { showBorder: true }
        }}
        hideSelectedOptions={false}
        isClearable={true}
        components={{
          SingleValue: (p) => (
            <SingleValueChip {...p} onDelete={(v) => props.selectProps.onClear()} />
          )
        }}
        onOptionDelete={(option) => {
          setAlertOpen(true);
          setOnAgreeAction(() => () => deleteSearchProfile(option.value._id));
        }}
        {...props.selectProps}
      />

      <AlertDialog
        open={alertOpen}
        title={`${locale.Delete}?`}
        onOk={() => onAgreeAction && onAgreeAction()}
        onClose={() => setAlertOpen(false)}
      >
        {locale.areYouSureYouWantToDelete(`${locale.SearchProfile.toLowerCase()}en`)}
      </AlertDialog>
    </>
  );
};
